package dev.lingvo.site.components.review

import dev.lingvo.shared.MAX_REVIEW_BATCH
import dev.lingvo.shared.ReviewAction
import dev.lingvo.shared.ReviewBatchItem
import dev.lingvo.shared.ReviewBatchRequest
import dev.lingvo.shared.ReviewBatchResponse
import dev.lingvo.shared.ReviewBatchResult
import dev.lingvo.shared.ReviewItemDto
import dev.lingvo.shared.SourceReviewBatchItem
import dev.lingvo.shared.SourceReviewBatchRequest
import dev.lingvo.site.api.Session

/**
 * The batch decision as data: what is being asked for, and what came back.
 *
 * Separate from the composable that draws it so the gate's arithmetic — who is covered, what the
 * preview promised, what the receipt says — can be tested without a browser or a server.
 */

/**
 * A batch decision bound to the exact queue, locale and versions the reviewer was looking at.
 *
 * The server's `canApprove` for this locale is the verified authority here: it already carries the
 * per-member reviewer override that a role alone cannot express, and it is computed by the same
 * server that will run the batch. The claim holds it and re-reads it at every suspension boundary,
 * the way a project action claim re-reads its route.
 */
internal class ReviewBatchClaim private constructor(
    val slug: String,
    val locale: String,
    /** The exact strings this batch names, in queue order, each with the version that was read. */
    val covered: List<ReviewItemDto>,
    /** More eligible strings were on screen than one batch may carry. */
    val capped: Boolean,
    val guarded: Boolean,
) {
    val sourceItems: List<SourceReviewBatchItem> = if (guarded) covered.map {
        SourceReviewBatchItem(it.keyId, checkNotNull(it.sourceSnapshot).view, checkNotNull(it.targetRevision))
    } else emptyList()
    val items: List<ReviewBatchItem> = covered.map {
        ReviewBatchItem(keyId = it.keyId, expectedUpdatedAt = it.updatedAt)
    }

    /**
     * Identity of *this* set at *these* versions.
     *
     * Remembered state is keyed on it, so a queue that reloads underneath an open preview discards
     * a preview that no longer describes what is on screen instead of confirming it.
     */
    val fingerprint: String = buildString {
        append(slug).append('|').append(locale)
        append('|').append(guarded)
        covered.forEach { append('|').append(it.keyId).append('@').append(it.updatedAt)
            .append('@').append(it.targetRevision).append('@').append(it.sourceSnapshot?.view) }
    }

    /** Re-read, never cached: the authority is the server's answer as of the last queue load. */
    fun remainsHeld(canApproveNow: Boolean): Boolean = canApproveNow && (!guarded || Session.serverFeatures.sourceAwareReview)

    fun request(dryRun: Boolean) = ReviewBatchRequest(
        items = items,
        action = ReviewAction.APPROVE,
        dryRun = dryRun,
    )

    fun sourceRequest(dryRun: Boolean) = SourceReviewBatchRequest(sourceItems, ReviewAction.APPROVE, dryRun = dryRun)

    /** The dry run, joined back to the key names and submitters it is talking about. */
    fun previewOf(response: ReviewBatchResponse): ReviewBatchPreview {
        val byId = covered.associateBy { it.keyId }
        return ReviewBatchPreview(
            counts = reviewBatchCounts(response),
            rows = response.outcomes.map { outcome ->
                val item = byId[outcome.keyId]
                ReviewBatchPreviewRow(
                    keyName = item?.keyName ?: outcome.keyId,
                    submittedBy = item?.submittedBy,
                    result = outcome.result,
                )
            },
            // Exactly what the preview said would go through, at the versions it checked. The run
            // cannot quietly grow past the set somebody agreed to.
            decidable = response.outcomes
                .filter { it.result == ReviewBatchResult.DECIDED }
                .map { ReviewBatchItem(keyId = it.keyId, expectedUpdatedAt = byId[it.keyId]?.updatedAt) },
            sourceDecidable = sourceItems.filter { original ->
                response.outcomes.any { it.keyId == original.keyId && it.result == ReviewBatchResult.DECIDED }
            },
        )
    }

    companion object {
        /** Null — so the control is absent — for anybody or anything the server would refuse. */
        fun capture(
            slug: String,
            locale: String,
            eligible: List<ReviewItemDto>,
            canApprove: Boolean,
            guardedMode: Boolean = false,
        ): ReviewBatchClaim? {
            if (!canApprove) return null
            if (eligible.any { !reviewSourceClaimAvailable(it, guardedMode = guardedMode) }) return null
            // Two is the smallest number for which "do this once instead of twice" is true. One
            // string already has a button of its own, on the card the reviewer is reading.
            if (eligible.size < 2) return null
            return ReviewBatchClaim(
                slug = slug,
                locale = locale,
                covered = eligible.take(MAX_REVIEW_BATCH),
                capped = eligible.size > MAX_REVIEW_BATCH,
                guarded = guardedMode || eligible.any { sourceReviewRequired(it) },
            )
        }
    }
}

/** What the server said it would do, per item, with nothing written. */
internal class ReviewBatchPreview(
    val counts: ReviewBatchCounts,
    val rows: List<ReviewBatchPreviewRow>,
    val decidable: List<ReviewBatchItem>,
    val sourceDecidable: List<SourceReviewBatchItem> = emptyList(),
) {
    /**
     * The part of this batch the run will not even attempt, and why.
     *
     * The receipt is about the batch somebody agreed to, not about the request that was sent. The
     * strings the preview already refused are never in the run's response — they were left out of
     * it — so a receipt built from the response alone said "every string in the batch went through"
     * on a batch where one of two was skipped.
     */
    val skipped = counts.copy(decided = 0)
}

/** Two halves of one batch: what the preview refused, plus what the run did. */
internal operator fun ReviewBatchCounts.plus(other: ReviewBatchCounts) = ReviewBatchCounts(
    decided = decided + other.decided,
    blocked = blocked + other.blocked,
    alreadyReviewed = alreadyReviewed + other.alreadyReviewed,
    stale = stale + other.stale,
    acknowledged = acknowledged + other.acknowledged,
    unconfirmed = unconfirmed || other.unconfirmed,
)

internal class ReviewBatchPreviewRow(
    val keyName: String,
    val submittedBy: String?,
    val result: ReviewBatchResult,
)

/** How a batch ended, counted once so the preview and the receipt cannot disagree. */
internal data class ReviewBatchCounts(
    val decided: Int,
    val blocked: Int,
    val alreadyReviewed: Int,
    val stale: Int,
    /** Internal handoff of the actual submitted claims, never guessed from an aggregate count. */
    val acknowledged: List<ReviewItemDto> = emptyList(),
    val unconfirmed: Boolean = false,
) {
    val skipped: Int get() = blocked + alreadyReviewed + stale
    val total: Int get() = decided + skipped
}

/** Pure, so the preview and the receipt can be tested against a response and nothing else. */
internal fun reviewBatchCounts(response: ReviewBatchResponse): ReviewBatchCounts = ReviewBatchCounts(
    decided = response.outcomes.count { it.result == ReviewBatchResult.DECIDED },
    blocked = response.outcomes.count { it.result == ReviewBatchResult.BLOCKED },
    alreadyReviewed = response.outcomes.count { it.result == ReviewBatchResult.ALREADY_REVIEWED },
    stale = response.outcomes.count { it.result == ReviewBatchResult.STALE },
)

/** Join a commit receipt to its immutable request; missing or duplicate outcomes are unresolved. */
internal fun reviewCommittedBatchCounts(response: ReviewBatchResponse, submitted: List<ReviewItemDto>): ReviewBatchCounts {
    if (response.dryRun) return ReviewBatchCounts(0, 0, 0, 0, unconfirmed = true)
    val byId = submitted.associateBy { it.keyId }
    val grouped = response.outcomes.groupBy { it.keyId }
    val identified = grouped.filter { (id, outcomes) -> id in byId && outcomes.size == 1 }.values.map { it.single() }
    val acknowledged = identified.filter { it.result == ReviewBatchResult.DECIDED }.map { byId.getValue(it.keyId) }
    return ReviewBatchCounts(
        decided = acknowledged.size,
        blocked = identified.count { it.result == ReviewBatchResult.BLOCKED },
        alreadyReviewed = identified.count { it.result == ReviewBatchResult.ALREADY_REVIEWED },
        stale = identified.count { it.result == ReviewBatchResult.STALE },
        acknowledged = acknowledged,
        unconfirmed = identified.size != submitted.size || response.outcomes.size != identified.size || response.decided != acknowledged.size,
    )
}
